import React, { useEffect, useState } from "react";

import BaseModal from "@/Components/BaseModal";
import VuiBox from "@/Components/VuiBox";

import ElectricalSwitchWindow from "@/Pages/ElectricalSwitchManagement/ElectricalSwitchWindow";

import {
  Catalog,
  ElectricalSwitch,
  ModelBrand,
} from "@/DatabaseControls/DataTypes";

import { useSnackbar } from "@/context/Snackbar/SnackbarContext";
import { useTranslation } from "react-i18next";

import { useAtom } from "jotai";
import { electricalSwitchModalState } from "@/states/catalogs/modals";

import { firebaseSingleton } from "@/DatabaseControls/FirebaseController";
import { getDefaultElectricalSwitch } from "@/DatabaseControls/DataDefaultValues";

interface ElectricalSwitchModalPropsType {
  catalogState: Catalog;
  setCatalogState: (catalog: Catalog) => void;
  refreshData: () => void;
  modelBrandList: ModelBrand[];
}

const ElectricalSwitchModal = (props: ElectricalSwitchModalPropsType) => {
  const { catalogState, setCatalogState, refreshData, modelBrandList } = props;

  const [electricalSwitchModal, setElectricalSwitchModal] = useAtom(
    electricalSwitchModalState,
  );
  const [electricalSwitchState, setElectricalSwitchState] =
    useState<ElectricalSwitch | null>(null);
  const [selectedElectricalSwitch, setSelectedElectricalSwitch] =
    useState<ElectricalSwitch | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const { setSnackbarProps, resetSnackbarProps } = useSnackbar();
  const { t } = useTranslation();

  const isEdit = !!selectedElectricalSwitch;

  useEffect(() => {
    if (electricalSwitchModal.isShow) {
      if (electricalSwitchModal.props.selectedElectricalSwitch) {
        setSelectedElectricalSwitch(
          electricalSwitchModal.props.selectedElectricalSwitch,
        );
        setElectricalSwitchState(
          electricalSwitchModal.props.selectedElectricalSwitch,
        );
      } else {
        setElectricalSwitchState(getDefaultElectricalSwitch());
      }

      setIsLoaded(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electricalSwitchModal.isShow, catalogState]);

  const saveElectricalSwitch = async () => {
    resetSnackbarProps();

    if (isEdit) {
      await firebaseSingleton
        .updateElectricalSwitch(electricalSwitchState)
        .then(() => {
          handleClose();
          refreshData();
        });

      setSnackbarProps({
        open: true,
        icon: "check_circle",
        content: "Electrical Switch record updated successfully.",
      });
    } else {
      const newElectricalSwitchId =
        firebaseSingleton.getNewDocumentId("electricalSwitch");

      await firebaseSingleton
        .addElectricalSwitch(
          electricalSwitchState,
          newElectricalSwitchId,
          catalogState.id,
        )
        .then(() => {
          handleClose();
          refreshData();

          setCatalogState({
            ...catalogState,
            ElectricalSwitches: [
              ...catalogState.ElectricalSwitches,
              newElectricalSwitchId,
            ],
          });
        });

      setSnackbarProps({
        open: true,
        icon: "check_circle",
        content: "Electrical Switch record created successfully.",
      });
    }
  };

  const deleteElectricalSwitch = async () => {
    if (selectedElectricalSwitch) {
      await firebaseSingleton
        .deleteElectricalSwitch(selectedElectricalSwitch.id, catalogState.id)
        .then(() => {
          handleClose();
          refreshData();

          setCatalogState({
            ...catalogState,
            ElectricalSwitches: [...catalogState.ElectricalSwitches],
          });

          setSnackbarProps({
            open: true,
            icon: "check_circle",
            content: "Electrical Switch record deleted successfully.",
          });
        });
    }
  };

  const handleClose = (): void => {
    setSelectedElectricalSwitch(null);

    setIsLoaded(false);

    setElectricalSwitchModal({
      ...electricalSwitchModal,
      isShow: false,
      props: {},
    });
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>): void => {
    saveElectricalSwitch();
  };

  return (
    <BaseModal
      title={
        selectedElectricalSwitch
          ? t("modules.electricalSwitches.section_titles.edit")
          : t("modules.electricalSwitches.section_titles.create")
      }
      open={electricalSwitchModal.isShow}
      onClose={handleClose}
      onDone={handleSubmit}
    >
      <VuiBox sx={{ minHeight: 600 }}>
        {isLoaded && (
          <ElectricalSwitchWindow
            electricalSwitchState={electricalSwitchState}
            setElectricalSwitchState={setElectricalSwitchState}
            modelBrandList={modelBrandList}
            onSave={saveElectricalSwitch}
            onDelete={deleteElectricalSwitch}
          />
        )}
      </VuiBox>
    </BaseModal>
  );
};

export default ElectricalSwitchModal;
